import { useState, useEffect } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  ShoppingCart,
  ShieldCheck,
  Zap,
  MapPin,
  ChevronRight,
  Lock,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useCart } from "@/hooks/use-cart";
import { useVat, VAT_RATE } from "@/hooks/use-vat";
import { formatEuro, applyDiscount } from "@/lib/catalog-format";
import { computeCartTotals } from "@/lib/totals";
// Mollie is uitgeschakeld — betalingen lopen via Stripe (zie /betalen/$orderId).
import { createOrder } from "@/lib/checkout.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";

export const Route = createFileRoute("/checkout")({
  head: () => ({
    meta: [
      { title: "Afrekenen — Elvion Group Nederland" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: CheckoutPage,
});

type Address = {
  id: string;
  label: string | null;
  street: string;
  house_number: string;
  postal_code: string;
  city: string;
  country: string;
};

import { SHIPPING } from "@/lib/company";
import { SHIPPING_POLICY_TEXT, ORDERING_ENABLED, ORDERING_DISABLED_TEXT, PRICE_ON_REQUEST, PRICE_ON_REQUEST_LABEL } from "@/lib/commerce";

/* Blue accent — scoped to this page only via inline CSS vars. */
const BLUE = "oklch(0.55 0.18 250)";
const BLUE_HOVER = "oklch(0.48 0.19 250)";
const BLUE_SOFT = "oklch(0.97 0.03 250)";
const SHIPPING_CENTS = SHIPPING.standardInclCents;

const FULFILLMENT_CONFIG = {
  delivery: {
    label: "PostNL regulier",
    costCents: SHIPPING_CENTS,
    displayValue: `PostNL regulier ${formatEuro(SHIPPING_CENTS)}`,
    displayClass: "",
  },
  pickup: {
    label: "Afhalen in Wageningen",
    costCents: 0,
    displayValue: "Gratis",
    displayClass: "text-emerald-600",
  },
} as const;

type FulfillmentKey = keyof typeof FULFILLMENT_CONFIG;
const FULFILLMENT_KEYS = Object.keys(FULFILLMENT_CONFIG) as FulfillmentKey[];

/** Verzendkosten volgens het gepubliceerde verzendbeleid: gratis vanaf €150 incl. btw. */
function shippingCostCents(fulfillment: FulfillmentKey, orderTotalInclCents: number): number {
  if (fulfillment === "pickup") return 0;
  return orderTotalInclCents >= SHIPPING.freeThresholdInclCents ? 0 : SHIPPING_CENTS;
}
const DEFAULT_FULFILLMENT: FulfillmentKey = "delivery";

/**
 * Versioned localStorage for fulfillment choice.
 * Bump CHECKOUT_FULFILLMENT_VERSION whenever the FULFILLMENT_CONFIG shape or
 * accepted keys change — older payloads are then ignored and the default is used.
 * Legacy plain-string entries (pre-versioning) are migrated when still valid.
 */
const CHECKOUT_FULFILLMENT_VERSION = 2;
const CHECKOUT_FULFILLMENT_KEY = "checkout_fulfillment_v2";
const CHECKOUT_FULFILLMENT_LEGACY_KEY = "checkout_fulfillment";

function isFulfillmentKey(v: unknown): v is FulfillmentKey {
  return typeof v === "string" && (FULFILLMENT_KEYS as string[]).includes(v);
}

function loadFulfillment(): FulfillmentKey {
  try {
    const raw = localStorage.getItem(CHECKOUT_FULFILLMENT_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { version?: number; value?: unknown };
      if (
        parsed &&
        parsed.version === CHECKOUT_FULFILLMENT_VERSION &&
        isFulfillmentKey(parsed.value)
      ) {
        return parsed.value;
      }
      // Stale / unknown version — discard.
      localStorage.removeItem(CHECKOUT_FULFILLMENT_KEY);
    }
    // Legacy migration: pre-v2 stored the raw string.
    const legacy = localStorage.getItem(CHECKOUT_FULFILLMENT_LEGACY_KEY);
    if (legacy) {
      localStorage.removeItem(CHECKOUT_FULFILLMENT_LEGACY_KEY);
      if (isFulfillmentKey(legacy)) return legacy;
    }
  } catch {
    // Corrupt JSON or storage unavailable — fall through to default.
  }
  return DEFAULT_FULFILLMENT;
}

function saveFulfillment(value: FulfillmentKey) {
  try {
    localStorage.setItem(
      CHECKOUT_FULFILLMENT_KEY,
      JSON.stringify({ version: CHECKOUT_FULFILLMENT_VERSION, value }),
    );
  } catch {}
}


function CheckoutPage() {
  const navigate = useNavigate();
  useEffect(() => {
    navigate({ to: "/offerte-aanvragen", replace: true });
  }, [navigate]);

  const { session, profile } = useAuth();
  const cart = useCart();
  useVat();
  const qc = useQueryClient();

  const approved = profile?.verification_status === "approved";
  const discountPct = approved ? Number(profile?.discount_percentage ?? 0) : 0;
  // Alle totalen komen uit één helper, zodat regel-totalen + subtotaal + btw
  // altijd exact op elkaar aansluiten (`incl = ex + vat`, geen `*1.21`).
  const totals = computeCartTotals(cart.items, discountPct, false);
  const subtotalExcl = totals.subtotalEx;
  const vatAmount = totals.vatAmount;
  const totalIncl = totals.subtotalIncl;

  const { data: addresses } = useQuery({
    queryKey: ["addresses", session?.user?.id ?? "guest"],
    enabled: !!session,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("addresses")
        .select("id, label, street, house_number, postal_code, city, country")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as Address[];
    },
  });

  const [selectedAddress, setSelectedAddress] = useState<string | "new">("new");
  const [newAddr, setNewAddr] = useState({
    street: "",
    house_number: "",
    postal_code: "",
    city: "",
    country: "Nederland",
  });
  const [guest, setGuest] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [business, setBusiness] = useState(false);
  const [differentShipping, setDifferentShipping] = useState(false);
  const [dropoffInstructions, setDropoffInstructions] = useState(false);
  const [notes, setNotes] = useState("");
  const [fulfillment, setFulfillment] = useState<FulfillmentKey>(DEFAULT_FULFILLMENT);
  useEffect(() => {
    setFulfillment(loadFulfillment());
  }, []);
  useEffect(() => {
    saveFulfillment(fulfillment);
  }, [fulfillment]);

  const [paymentMethod, setPaymentMethod] = useState<"wero" | "card" | "cod">("wero");
  const [agreed, setAgreed] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const DEPOSIT_CENTS = 2500;
  const createOrderFn = useServerFn(createOrder);

  useEffect(() => {
    if (addresses && addresses.length > 0 && selectedAddress === "new") {
      setSelectedAddress(addresses[0].id);
    }
  }, [addresses]);

  const resolvedAddress =
    newAddr.street && newAddr.postal_code && newAddr.city
      ? `${newAddr.street} ${newAddr.house_number}, ${newAddr.postal_code} ${newAddr.city}`
      : null;

  const outOfStockItems = cart.items.filter((i) => i.in_stock === false);
  const hasOutOfStock = outOfStockItems.length > 0;

  const placeOrder = useMutation({
    mutationFn: async () => {
      if (cart.items.length === 0) throw new Error("Je winkelmand is leeg");
      if (hasOutOfStock) throw new Error("Verwijder producten die niet op voorraad zijn om af te rekenen");
      if (!agreed) throw new Error("Ga akkoord met de voorwaarden");
      const fullName = `${guest.firstName} ${guest.lastName}`.trim();
      if (!session && (!guest.email || !fullName)) {
        throw new Error("Vul je naam en e-mailadres in");
      }
      if (fulfillment === "delivery" && selectedAddress === "new") {
        if (
          !newAddr.street ||
          !newAddr.house_number ||
          !newAddr.postal_code ||
          !newAddr.city
        ) {
          throw new Error("Vul het volledige adres in");
        }
      }

      const result = await createOrderFn({
        data: {
          items: cart.items,
          guest: session
            ? undefined
            : {
                name: fullName,
                email: guest.email,
                phone: guest.phone || undefined,
              },
          newAddress:
            fulfillment === "delivery" && selectedAddress === "new"
              ? newAddr
              : undefined,
          selectedAddressId:
            fulfillment === "delivery" && selectedAddress !== "new"
              ? selectedAddress
              : undefined,
          fulfillment,
          notes: notes || undefined,
          userId: profile?.id ?? undefined,
        },
      });

      cart.clear();
      qc.invalidateQueries({ queryKey: ["my-orders"] });
      navigate({ to: "/betalen/$orderId", params: { orderId: result.orderId } });
      return result;
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!ORDERING_ENABLED) {
    return (
      <>
        <Header />
        <div className="container mx-auto max-w-3xl px-4 py-12">
          <div className="rounded-xl border border-border bg-card p-10 text-center">
            <h1 className="text-2xl font-bold">Bestellen is tijdelijk niet mogelijk</h1>
            <p className="mx-auto mt-3 max-w-md text-sm text-text-secondary">
              {ORDERING_DISABLED_TEXT}
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              <Button asChild>
                <Link to="/offerte-aanvragen">Offerte aanvragen</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/contact">Contact opnemen</Link>
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (cart.items.length === 0) {
    return (
      <>
        <Header />
        <div className="container mx-auto max-w-3xl px-4 py-12">
          <div className="rounded-xl border border-border bg-card p-10 text-center">
            <ShoppingCart className="mx-auto size-10 text-text-secondary" />
            <h1 className="mt-3 text-2xl font-bold">Je winkelmand is leeg</h1>
            <p className="mt-2 text-sm text-text-secondary">
              Voeg producten toe om verder te gaan met afrekenen.
            </p>
            <p className="mx-auto mt-4 max-w-md text-xs text-text-secondary">
              {SHIPPING_POLICY_TEXT}
            </p>
            <Button asChild className="mt-6">
              <Link to="/c">Verder winkelen</Link>
            </Button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  /* Reusable section card */
  const SectionCard = ({
    title,
    children,
  }: {
    title: string;
    children: React.ReactNode;
  }) => (
    <section className="rounded-2xl border border-border bg-card p-6">
      <h2 className="mb-5 text-sm font-semibold tracking-wide text-foreground">
        {title}
      </h2>
      {children}
    </section>
  );

  return (
    <>
      <Header />
      <div
        className="bg-background"
        style={
          {
            ["--checkout-accent" as string]: BLUE,
            ["--checkout-accent-hover" as string]: BLUE_HOVER,
            ["--checkout-accent-soft" as string]: BLUE_SOFT,
          } as React.CSSProperties
        }
      >
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="mb-6 flex items-center gap-2 text-sm text-text-secondary">
          <Link to="/" className="hover:text-foreground">
            Home
          </Link>
          <ChevronRight className="size-3.5" />
          <Link to="/c" className="hover:text-foreground">
            Winkelwagen
          </Link>
          <ChevronRight className="size-3.5" />
          <span className="font-medium text-foreground">Checkout</span>
        </nav>

        <h1 className="mb-8 text-4xl font-bold tracking-tight">U bent er bijna…</h1>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* LEFT — form */}
          <div className="space-y-6 lg:col-span-2">
            {/* Contactgegevens */}
            <SectionCard title="Contactgegevens">
              <div className="mb-4 flex justify-end">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={business}
                    onChange={(e) => setBusiness(e.target.checked)}
                    className="accent-[color:var(--checkout-accent)]"
                  />
                  Zakelijk bestellen
                </label>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="firstName">Voornaam *</Label>
                  <Input
                    id="firstName"
                    value={guest.firstName}
                    onChange={(e) =>
                      setGuest({ ...guest, firstName: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="lastName">Achternaam *</Label>
                  <Input
                    id="lastName"
                    value={guest.lastName}
                    onChange={(e) =>
                      setGuest({ ...guest, lastName: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="email">E-mailadres *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={guest.email}
                    onChange={(e) =>
                      setGuest({ ...guest, email: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Telefoonnummer *</Label>
                  <Input
                    id="phone"
                    value={guest.phone}
                    onChange={(e) =>
                      setGuest({ ...guest, phone: e.target.value })
                    }
                  />
                </div>
              </div>
            </SectionCard>

            {/* Factuur / Verzendadres */}
            <SectionCard title="Factuur / Verzendadres">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="postal_code">Postcode *</Label>
                  <Input
                    id="postal_code"
                    value={newAddr.postal_code}
                    onChange={(e) =>
                      setNewAddr({ ...newAddr, postal_code: e.target.value })
                    }
                    placeholder="1234AB"
                  />
                </div>
                <div>
                  <Label htmlFor="house_number">Huisnr. + toevoeging *</Label>
                  <Input
                    id="house_number"
                    value={newAddr.house_number}
                    onChange={(e) =>
                      setNewAddr({ ...newAddr, house_number: e.target.value })
                    }
                    placeholder="12a"
                  />
                </div>
                <div className="sm:col-span-2 grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="street">Straat *</Label>
                    <Input
                      id="street"
                      value={newAddr.street}
                      onChange={(e) =>
                        setNewAddr({ ...newAddr, street: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">Plaats *</Label>
                    <Input
                      id="city"
                      value={newAddr.city}
                      onChange={(e) =>
                        setNewAddr({ ...newAddr, city: e.target.value })
                      }
                    />
                  </div>
                </div>
                {resolvedAddress && (
                  <div className="sm:col-span-2 flex items-center gap-2 rounded-lg bg-muted/60 px-3 py-2.5 text-sm">
                    <MapPin className="size-4 text-text-secondary" />
                    <span>{resolvedAddress}</span>
                  </div>
                )}
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={differentShipping}
                    onChange={(e) => setDifferentShipping(e.target.checked)}
                    className="accent-[color:var(--checkout-accent)]"
                  />
                  Afwijkend verzendadres
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={dropoffInstructions}
                    onChange={(e) => setDropoffInstructions(e.target.checked)}
                    className="accent-[color:var(--checkout-accent)]"
                  />
                  Neerzettoestemming / instructies
                </label>
                {dropoffInstructions && (
                  <textarea
                    className="sm:col-span-2 mt-2 w-full rounded-md border border-border bg-background p-3 text-sm"
                    rows={2}
                    placeholder="Bijv. afleverinstructies"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                )}
              </div>
            </SectionCard>

            {/* Bezorgmethode */}
            <SectionCard title="Bezorgmethode">
              <div className="space-y-3">
                <RadioCard
                  selected={fulfillment === "delivery"}
                  onClick={() => setFulfillment("delivery")}
                  title={`${FULFILLMENT_CONFIG.delivery.label} ${formatEuro(FULFILLMENT_CONFIG.delivery.costCents)}`}
                  subtitle={`Levertijd ${SHIPPING.deliveryNl} · alleen binnen Nederland gratis vanaf ${formatEuro(SHIPPING.freeThresholdInclCents)} incl. btw`}
                />
                <RadioCard
                  selected={fulfillment === "pickup"}
                  onClick={() => setFulfillment("pickup")}
                  title="Afhalen in onze winkel (gratis)"
                  subtitle="Haal uw bestelling op in onze winkel in Wageningen"
                />
              </div>
            </SectionCard>

            {/* Betaalmethode */}
            <SectionCard title="Betaalmethode">
              <div className="space-y-3">
                <RadioCard
                  selected={paymentMethod === "wero"}
                  onClick={() => setPaymentMethod("wero")}
                  title="WERO / iDEAL"
                />
                <RadioCard
                  selected={paymentMethod === "card"}
                  onClick={() => setPaymentMethod("card")}
                  title="Creditcard"
                />
                {fulfillment === "pickup" && !PRICE_ON_REQUEST && (
                  <RadioCard
                    selected={paymentMethod === "cod"}
                    onClick={() => setPaymentMethod("cod")}
                    title="Betalen bij levering"
                    subtitle={`Aanbetaling van ${formatEuro(DEPOSIT_CENTS)} online — restant (${formatEuro(
                      Math.max(0, totalIncl - DEPOSIT_CENTS),
                    )}) per pin bij levering`}
                  />
                )}
              </div>
            </SectionCard>

            {/* Voorwaarden */}
            <SectionCard title="">
              <label className="flex items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-1 accent-[color:var(--checkout-accent)]"
                />
                <span>
                  Ik ga akkoord met de{" "}
                  <Link
                    to="/voorwaarden"
                    className="underline"
                    style={{ color: "var(--checkout-accent)" }}
                  >
                    algemene voorwaarden
                  </Link>
                  , het{" "}
                  <Link
                    to="/retourbeleid"
                    className="underline"
                    style={{ color: "var(--checkout-accent)" }}
                  >
                    retourbeleid
                  </Link>
                  , de{" "}
                  <Link
                    to="/privacy"
                    className="underline"
                    style={{ color: "var(--checkout-accent)" }}
                  >
                    privacyverklaring
                  </Link>{" "}
                  en de{" "}
                  <Link
                    to="/verzendbeleid"
                    className="underline"
                    style={{ color: "var(--checkout-accent)" }}
                  >
                    betaalverplichting
                  </Link>
                  . Ik ben op de hoogte van mijn{" "}
                  <Link
                    to="/retourbeleid"
                    className="underline"
                    style={{ color: "var(--checkout-accent)" }}
                  >
                    herroepingsrecht van 14 dagen
                  </Link>
                  . *
                </span>
              </label>
            </SectionCard>

            {/* Kortingscode */}
            <SectionCard title="Kortingscode">
              <div className="flex gap-2">
                <Input
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value)}
                  placeholder="Voer je kortingscode in"
                  className="flex-1"
                />
                <button
                  type="button"
                  onClick={() =>
                    toast.info("Kortingscode niet geldig of niet beschikbaar")
                  }
                  className="rounded-md px-5 text-sm font-semibold text-white transition-colors"
                  style={{ backgroundColor: "var(--checkout-accent)" }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      "var(--checkout-accent-hover)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      "var(--checkout-accent)")
                  }
                >
                  Toepassen
                </button>
              </div>
            </SectionCard>
          </div>

          {/* RIGHT — summary */}
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-2xl border border-border bg-card p-6">
              <h2 className="mb-4 text-base font-semibold">Besteloverzicht</h2>

              {!PRICE_ON_REQUEST && (
                <div className="mb-2 flex items-baseline justify-between text-[11px] uppercase tracking-wide text-text-secondary">
                  <span>Artikel</span>
                  <span>Prijs (excl. btw)</span>
                </div>
              )}
              <ul className="space-y-3 border-b border-border pb-4">
                {cart.items.map((it, idx) => {
                  const line = totals.lines[idx];
                  return (
                    <li
                      key={it.slug}
                      className="flex items-start justify-between gap-3 text-sm"
                    >
                      <span>
                        {it.qty}x {it.name}
                        {it.in_stock === false && (
                          <span className="ml-2 inline-block rounded bg-red-100 px-1.5 py-0.5 text-[11px] font-medium text-red-700">
                            Niet op voorraad
                          </span>
                        )}
                      </span>
                      <span className="whitespace-nowrap font-medium">
                        {PRICE_ON_REQUEST ? PRICE_ON_REQUEST_LABEL : formatEuro(line.lineEx)}
                      </span>
                    </li>
                  );
                })}
              </ul>

              {!PRICE_ON_REQUEST && (
                <div className="space-y-2 border-b border-border py-4 text-sm">
                  <Row
                    label="Subtotaal excl. btw"
                    value={formatEuro(subtotalExcl)}
                  />
                  <Row label={`Btw (${Math.round(VAT_RATE * 100)}%)`} value={formatEuro(vatAmount)} />
                  <Row
                    label="Subtotaal incl. btw"
                    value={formatEuro(totalIncl)}
                  />
                  <Row
                    label="Verzending"
                    value={
                      <span
                        className={
                          shippingCostCents(fulfillment, totalIncl) === 0
                            ? "text-emerald-600"
                            : FULFILLMENT_CONFIG[fulfillment].displayClass || ""
                        }
                      >
                        {shippingCostCents(fulfillment, totalIncl) === 0
                          ? fulfillment === "pickup"
                            ? "Gratis"
                            : "Gratis (NL vanaf €150)"
                          : FULFILLMENT_CONFIG[fulfillment].displayValue}
                      </span>
                    }
                  />
                </div>
              )}

              <div className="flex items-baseline justify-between pt-4 pb-1">
                <span className="text-lg font-bold">Totaal</span>
                <span className="text-2xl font-bold">
                  {PRICE_ON_REQUEST ? PRICE_ON_REQUEST_LABEL : formatEuro(totalIncl + shippingCostCents(fulfillment, totalIncl))}
                </span>
              </div>
              {!PRICE_ON_REQUEST && (
                <p className="pb-1 text-right text-[11px] text-text-secondary">
                  Incl. {Math.round(VAT_RATE * 100)}% btw
                </p>
              )}
              <p className="pb-5 text-[11px] text-text-secondary">
                {SHIPPING_POLICY_TEXT}
              </p>

              {hasOutOfStock && (
                <div className="mb-3 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                  Eén of meer producten in je winkelwagen zijn niet op voorraad. Verwijder deze producten om af te rekenen.
                </div>
              )}
              <button
                type="button"
                onClick={() => placeOrder.mutate()}
                disabled={placeOrder.isPending || hasOutOfStock}
                className="flex w-full items-center justify-center gap-2 rounded-md py-3.5 text-sm font-semibold text-white shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                style={{ backgroundColor: "var(--checkout-accent)" }}
                onMouseEnter={(e) => {
                  if (!placeOrder.isPending && !hasOutOfStock)
                    e.currentTarget.style.backgroundColor =
                      "var(--checkout-accent-hover)";
                }}
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    "var(--checkout-accent)")
                }
              >
                <Lock className="size-4" />
                {placeOrder.isPending
                  ? "Bezig…"
                  : hasOutOfStock
                    ? "Niet op voorraad"
                    : "Bestelling afrekenen"}
              </button>

              <div className="mt-4 flex items-center justify-center gap-5 text-xs text-text-secondary">
                <span className="flex items-center gap-1.5">
                  <ShieldCheck className="size-4" />
                  Veilig betalen
                </span>
                <span className="flex items-center gap-1.5">
                  <Zap className="size-4" />
                  Direct verwerkt
                </span>
              </div>

              <div className="mt-4 flex items-center justify-center">
                <div
                  className="flex h-9 w-9 items-center justify-center rounded-full"
                  style={{ backgroundColor: "var(--checkout-accent-soft)" }}
                >
                  <span
                    className="text-sm font-bold"
                    style={{ color: "var(--checkout-accent)" }}
                  >
                    M
                  </span>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
    <Footer />
  </>
  );
}

function Row({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-text-secondary">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

function RadioCard({
  selected,
  onClick,
  title,
  subtitle,
}: {
  selected: boolean;
  onClick: () => void;
  title: string;
  subtitle?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-start gap-3 rounded-lg border p-4 text-left transition-colors"
      style={{
        borderColor: selected ? "var(--checkout-accent)" : undefined,
        backgroundColor: selected ? "var(--checkout-accent-soft)" : undefined,
      }}
    >
      <span
        className="mt-0.5 grid size-4 shrink-0 place-items-center rounded-full border"
        style={{
          borderColor: selected
            ? "var(--checkout-accent)"
            : "var(--border)",
        }}
      >
        {selected && (
          <span
            className="size-2 rounded-full"
            style={{ backgroundColor: "var(--checkout-accent)" }}
          />
        )}
      </span>
      <div className="text-sm">
        <div className="font-semibold">{title}</div>
        {subtitle && (
          <div className="mt-0.5 text-text-secondary">{subtitle}</div>
        )}
      </div>
    </button>
  );
}
